import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import showToast from "../../utils/toastNotifications";
import { useThunk } from "../../hooks/useThunk";
import {
  getPendingCustomers,
  approveCustomer,
  deleteCustomer,
} from "../../services/thunks/customerThunks";

import Table from "../../components/Table/Table";
import ConfirmDeleteModal from "../../components/Modals/ConfirmDeleteModal";

import deleteIcon from "../../assets/icons/delete.svg";
import viewIcon from "../../assets/icons/view.svg";
import approveIcon from "../../assets/icons/approve.svg";
import searchIcon from "../../assets/icons/search.svg";
import { useNavigate } from "react-router-dom";

const PendingCustomerRequests = () => {
  const auth = useSelector((state) => state.auth);

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [approvedCustomers, setApprovedCustomers] = useState(new Set());

  const [doDeleteCustomer, isLoadingDelete] = useThunk(deleteCustomer);
  const [doApproveCustomer, isLoadingApprove] = useThunk(approveCustomer);

  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const tableRef = useRef(null);
  const navigate = useNavigate();

  const handleDelete = async (id) => {
    const result = await doDeleteCustomer({ token: auth.token, id});
    if (result.success) {
      showToast("success", "Pending request deleted successfully");
      setRefreshTrigger((prev) => prev + 1);
    } else {
      showToast("error", result.error.message);
    }
  };

  const handleApprove = async (customer) => {
    const result = await doApproveCustomer({ token: auth.token, id: customer._id });
    if (result.success) {
      showToast("success", "Customer approved successfully");
      setApprovedCustomers(prev => new Set([...prev, customer._id]));
     
    } else {
      showToast("error", result.error.message);
    }
  };

  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleViewCustomer = (customer) => {
    navigate(`/customer/${customer._id}`);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tableRef.current && !tableRef.current.contains(event.target)) {
        // Reset any row states if needed
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const tableColumns = useMemo(
    () => [
      {
        title: <div style={{ paddingLeft: '2.375rem' }}>User ID</div>,
        dataIndex: "userId",
        key: "userId",
        minWidth: "100px",
        render: (text) => (
          <div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2] pl-[2.375rem]">
            {text}
          </div>
        ),
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        minWidth: "150px",
        render: (text) => (
          <div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2]">
            {text || "N/A"}
          </div>
        ),
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        minWidth: "200px",
        render: (text) => (
          <div className="min-h-[32px] flex items-center text-sm font-[400] font-['Public Sans'] text-[#E2E2E2]">
            {text}
          </div>
        ),
      },
      {
        title: "DJ Name",
        dataIndex: "djName",
        key: "djName",
        minWidth: "150px",
        render: (text) => (
          <div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2]">
            {text || "N/A"}
          </div>
        ),
      },
      {
        title: "Requested On",
        dataIndex: "requestedOn",
        key: "requestedOn",
        minWidth: "180px",
        render: (date) => {
          const formattedDate = date ? (() => {
            const dateObj = new Date(date);
            const datePart = dateObj.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            });
            const timePart = dateObj.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            });
            return `${datePart} – ${timePart}`;
          })() : 'N/A';
          
          return (
            <div className="min-h-[32px] flex items-center text-sm font-[400] font-['Public Sans'] text-[#E2E2E2]">
              {formattedDate}
            </div>
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        minWidth: "250px",
        render: (_, record) => {
          const isApproved = record.approved || approvedCustomers.has(record._id);
          
          return (
            <div className="flex gap-[8px] justify-start items-center">
              {!isApproved ? (
                <button
                  onClick={() => handleApprove(record)}
                  className={`flex items-center justify-center gap-[5px] px-[12px] py-[2px] border border-[#52FAF0] rounded-[15px] text-[12px] text-[#52FAF0] w-[95px] ${isLoadingApprove ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={isLoadingApprove}
                >
                  <img
                    src={approveIcon}
                    alt="approve"
                    className="w-[10px] h-[11px]"
                  />
                  Approve
                </button>
              ) : (
                <div className="flex items-center justify-center px-[12px] py-[2px] text-[12px] text-[#52FAF0] w-[95px]">
                  Yes
                </div>
              )}
              <button
                onClick={() => handleViewCustomer(record)}
                className="flex items-center justify-center gap-[5px] px-[12px] py-[2px] border border-[#07C01D] rounded-[15px] text-[12px] text-[#07C01D] w-[75px]"
              >
                <img
                  src={viewIcon}
                  alt="view"
                  className="w-[10px] h-[11px]"
                />
                View
              </button>
              <button
                onClick={() => {
                  setSelectedCustomer(record);
                  setShowConfirm(true);
                }}
                className={`flex items-center justify-center gap-[5px] px-[12px] py-[2px] border border-[#C00707] rounded-[15px] text-[12px] text-[#C00707] w-[65px] ${isLoadingDelete ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isLoadingDelete}
              >
                <img
                  src={deleteIcon}
                  alt="delete"
                  className="w-[10px] h-[12px]"
                />
                Delete
              </button>
            </div>
          );
        },
      },
    ],
    [auth.token, doDeleteCustomer, doApproveCustomer, approvedCustomers]
  );

  return (
    <div className="px-6 py-10 text-white min-h-screen">
      {/* Search Section */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center relative bg-[#2D2D2D] border border-[#808080] rounded-[5px] px-3 w-full h-[45px]">
          <img src={searchIcon} alt="search" className="w-5 h-5" />
          <input
            type="text"
            placeholder="Search by name, email, or ID"
            className="bg-transparent outline-none text-sm ml-3 w-full text-[#E2E2E2] font-[400] font-['Public Sans'] pr-6"
            value={searchQuery}
            onChange={(e) => {
              const value = e.target.value;
              setSearchQuery(value);
              if (value.trim() === "") {
                setRefreshTrigger((prev) => prev + 1);
              }
            }}
          />
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery("");
                setRefreshTrigger((prev) => prev + 1);
              }}
              className="absolute right-3 text-[#A0A0A0] hover:text-white text-lg font-bold"
            >
              ×
            </button>
          )}
        </div>

        <button
          onClick={handleSearch}
          className="border border-[#51F9EF] rounded-[3.77px] h-[45px] w-[91px] text-[#51F9EF] text-[11px] uppercase font-['Public Sans']"
        >
          Search
        </button>
      </div>

      {/* Table Section */}
      <div ref={tableRef}>
        <Table
          columns={tableColumns}
          fetchData={getPendingCustomers}
          refreshTrigger={refreshTrigger}
          headingBgColor="#0D141B"
          headingTextColor="#FFFFFF"
          customQueryParams={(params) => ({
            ...params,
            token: auth.token,
            search: searchQuery.trim() || undefined,
          })}
        />
      </div>

      <ConfirmDeleteModal
        visible={showConfirm}
        onConfirm={async () => {
          await handleDelete(selectedCustomer?._id);
          setShowConfirm(false);
          setSelectedCustomer(null);
        }}
        onCancel={() => {
          setShowConfirm(false);
          setSelectedCustomer(null);
        }}
        title="Confirm Delete"
        content="Are you sure you want to delete this request?"
      />
    </div>
  );
};

export default PendingCustomerRequests;